import { useState, useEffect, useCallback, useRef } from "react";

const TRACKING_STORAGE_KEY = "sati_pending_tracking";

/**
 * Hook to handle resilient tracking persistence and retries.
 */
export const useTrackingPersistor = (saveFunction) => {
  const [pendingEvents, setPendingEvents] = useState(() => {
    try {
      const saved = localStorage.getItem(TRACKING_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to load pending tracking events", e);
      return [];
    }
  });

  const isSaving = useRef(false);

  // Sync with localStorage whenever pendingEvents change
  useEffect(() => {
    try {
      localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(pendingEvents));
    } catch (e) {
      console.error("Failed to save pending tracking events to localStorage", e);
    }
  }, [pendingEvents]);

  const addEvents = useCallback((events) => {
    if (!events || events.length === 0) return;
    setPendingEvents((prev) => [...prev, ...events]);
  }, []);

  const persist = useCallback(async () => {
    if (isSaving.current || pendingEvents.length === 0) return;

    isSaving.current = true;
    const eventsToSave = [...pendingEvents];

    try {
      await saveFunction(eventsToSave);
      // If success, remove the specific events we tried to save
      setPendingEvents((prev) => 
        prev.filter(event => !eventsToSave.includes(event))
      );
    } catch (error) {
      console.warn("Failed to persist tracking events. Will retry later.", error);
      // Keep events in state for next attempt
    } finally {
      isSaving.current = false;
    }
  }, [pendingEvents, saveFunction]);

  return {
    pendingEvents,
    addEvents,
    persist,
    hasPending: pendingEvents.length > 0
  };
};
